import { useEffect, useRef, useState } from "react";
import { Coin3D } from "../ui/AnimatedScene";

const LAUNCH_DATE = new Date("2026-03-31T23:59:59");

function calcTimeLeft() {
  const diff = LAUNCH_DATE - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days:    Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours:   Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

// ─── Particle Network Canvas ───────────────────────────────────────────────────
function ParticleCanvas({ mouseRef }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let W, H, particles;

    const COLORS = ["#10B77F", "#60A5FA", "#A855F7", "#FBBF24", "#34D399"];

    function init() {
      W = canvas.offsetWidth;
      H = canvas.offsetHeight;
      canvas.width  = W;
      canvas.height = H;
      const COUNT = Math.max(40, Math.floor((W * H) / 15000));
      particles = Array.from({ length: COUNT }, () => ({
        x:     Math.random() * W,
        y:     Math.random() * H,
        z:     Math.random(),                      // depth 0→1
        vx:    (Math.random() - 0.5) * 0.5,
        vy:    (Math.random() - 0.5) * 0.5,
        r:     Math.random() * 2 + 1,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        pulse: Math.random() * Math.PI * 2,
        opac:  Math.random() * 0.45 + 0.25,
      }));
    }

    function hexToRgb(hex) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `${r},${g},${b}`;
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const MAX_CONN = 150;
      const MOUSE_R  = 130;

      // Update positions
      for (const p of particles) {
        p.pulse += 0.018;

        // Mouse repulsion
        const dx = p.x - mx;
        const dy = p.y - my;
        const d2 = dx * dx + dy * dy;
        if (d2 < MOUSE_R * MOUSE_R && d2 > 0) {
          const d    = Math.sqrt(d2);
          const f    = ((MOUSE_R - d) / MOUSE_R) * 0.35;
          p.vx += (dx / d) * f;
          p.vy += (dy / d) * f;
        }

        p.vx *= 0.985;
        p.vy *= 0.985;
        p.x  += p.vx;
        p.y  += p.vy;

        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;
      }

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < MAX_CONN) {
            const alpha = (1 - d / MAX_CONN) * 0.22 * particles[i].z * particles[j].z;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(16,183,127,${alpha})`;
            ctx.lineWidth   = 0.7;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        const glow   = Math.sin(p.pulse) * 0.2 + 0.8;
        const radius = p.r * glow * (0.5 + p.z * 0.5);
        const rgb    = hexToRgb(p.color);

        // Halo glow
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius * 7);
        grad.addColorStop(0, `rgba(${rgb},${p.opac * 0.5 * p.z})`);
        grad.addColorStop(1, `rgba(${rgb},0)`);
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius * 7, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Core dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb},${p.opac * (0.6 + p.z * 0.4)})`;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    }

    init();
    draw();

    const onResize = () => { init(); };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, [mouseRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
        opacity: 0.85,
      }}
    />
  );
}

// ─── Flip-clock Countdown Block ───────────────────────────────────────────────
function CountBlock({ value, label }) {
  const display = String(value).padStart(2, "0");
  const [prev, setPrev]     = useState(display);
  const [flipping, setFlip] = useState(false);
  const [hovered, setHov]   = useState(false);

  useEffect(() => {
    if (display !== prev) {
      setFlip(true);
      const t = setTimeout(() => { setPrev(display); setFlip(false); }, 350);
      return () => clearTimeout(t);
    }
  }, [display, prev]);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div style={{
        position: "relative",
        width:  "clamp(68px, 9vw, 108px)",
        height: "clamp(76px, 10vw, 120px)",
        borderRadius: 18,
        background: hovered ? "rgba(16,183,127,0.08)" : "rgba(8,8,14,0.88)",
        border: `1px solid ${hovered ? "rgba(16,183,127,0.5)" : "rgba(16,183,127,0.2)"}`,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: hovered
          ? "0 0 0 1px rgba(16,183,127,0.1) inset, 0 0 40px rgba(16,183,127,0.2), 0 12px 40px rgba(0,0,0,0.7)"
          : "0 0 0 1px rgba(255,255,255,0.04) inset, 0 8px 32px rgba(0,0,0,0.6), 0 0 20px rgba(16,183,127,0.06)",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        transform: hovered ? "translateY(-4px) scale(1.06)" : "translateY(0) scale(1)",
        transition: "all 0.35s cubic-bezier(0.16,1,0.3,1)",
        cursor: "default",
      }}>
        <div style={{
          position: "absolute", top: 0, left: "8%", right: "8%", height: 1,
          background: `linear-gradient(90deg,transparent,rgba(16,183,127,${hovered ? 0.9 : 0.4}),transparent)`,
          transition: "opacity 0.3s",
        }} />
        <div style={{
          position: "absolute", left: 0, right: 0, top: "50%",
          height: 1, background: "rgba(0,0,0,0.5)", zIndex: 2,
        }} />
        <div style={{
          position: "absolute", bottom: 0, left: "20%", right: "20%", height: 2,
          borderRadius: "2px 2px 0 0",
          background: hovered
            ? "linear-gradient(90deg,transparent,rgba(16,183,127,0.8),transparent)"
            : "transparent",
          transition: "all 0.35s",
        }} />
        <span style={{
          fontFamily: "Space Grotesk, sans-serif",
          fontWeight: 800,
          fontSize: "clamp(1.9rem, 4.5vw, 3rem)",
          letterSpacing: "-0.05em",
          color: hovered ? "#fff" : "var(--text)",
          textShadow: hovered
            ? "0 0 30px rgba(16,183,127,0.7), 0 0 60px rgba(16,183,127,0.3)"
            : "0 0 20px rgba(16,183,127,0.3)",
          transform: flipping ? "translateY(-6px) rotateX(20deg)" : "translateY(0) rotateX(0deg)",
          opacity: flipping ? 0.4 : 1,
          transition: "transform 0.32s cubic-bezier(0.4,0,0.2,1), opacity 0.32s, color 0.3s, text-shadow 0.3s",
          position: "relative", zIndex: 3,
          display: "block",
        }}>
          {prev}
        </span>
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: "48%",
          background: "rgba(255,255,255,0.028)",
          borderRadius: "18px 18px 0 0",
          pointerEvents: "none",
        }} />
      </div>
      <span style={{
        fontFamily: "Space Grotesk, sans-serif",
        fontSize: "clamp(0.54rem, 0.9vw, 0.65rem)",
        fontWeight: 700, letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: hovered ? "var(--gold)" : "var(--text-muted)",
        transition: "color 0.3s",
      }}>
        {label}
      </span>
    </div>
  );
}

function Colon() {
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      gap: "clamp(5px,1.2vw,10px)", paddingBottom: 22, alignSelf: "center",
    }}>
      {[0, 1].map(i => (
        <div key={i} style={{
          width: "clamp(4px,0.7vw,5px)", height: "clamp(4px,0.7vw,5px)",
          borderRadius: "50%",
          background: "rgba(16,183,127,0.5)",
          boxShadow: "0 0 8px rgba(16,183,127,0.5)",
          animation: "pulseGold 1.8s ease-in-out infinite",
          animationDelay: `${i * 0.35}s`,
        }} />
      ))}
    </div>
  );
}

// ─── 3D Coin Scene ─────────────────────────────────────────────────────────────
function CoinScene3D({ mouseRef }) {
  const containerRef = useRef(null);
  const [tilt, setTilt]   = useState({ x: 0, y: 0 });
  const [hovered, setHov] = useState(false);

  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dx = (e.clientX - (rect.left + rect.width  / 2)) / (rect.width  / 2);
    const dy = (e.clientY - (rect.top  + rect.height / 2)) / (rect.height / 2);
    setTilt({ x: dy * -14, y: dx * 14 });
    if (mouseRef) mouseRef.current = { x: e.clientX, y: e.clientY };
  };

  const SIZE = 220;

  const ORBS = [
    { w: SIZE + 130, h: SIZE + 130, border: "rgba(16,183,127,0.3)",  spin: "12s",  rev: false, dot: { s: 12, c: "#10B77F" }, tilt: "none" },
    { w: SIZE + 100, h: SIZE + 100, border: "rgba(96,165,250,0.22)", spin: "17s",  rev: true,  dot: { s: 9,  c: "#60A5FA" }, tilt: "rotateX(65deg) rotateZ(30deg)" },
    { w: SIZE + 65,  h: SIZE + 65,  border: "rgba(168,85,247,0.18)", spin: "10s",  rev: false, dot: { s: 7,  c: "#A855F7" }, tilt: "rotateX(45deg) rotateZ(-20deg)" },
    { w: SIZE + 28,  h: SIZE + 28,  border: "rgba(110,231,183,0.1)", spin: "6s",   rev: true,  dot: null,                   tilt: "none", dashed: true },
  ];

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => { setTilt({ x: 0, y: 0 }); setHov(false); }}
      style={{
        position: "relative",
        width: SIZE + 200, height: SIZE + 200,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "none",
      }}
    >
      {/* Atmospheric bloom */}
      <div style={{
        position: "absolute",
        width: SIZE + 180, height: SIZE + 180,
        borderRadius: "50%",
        background: hovered
          ? "radial-gradient(circle,rgba(16,183,127,0.22) 0%,transparent 65%)"
          : "radial-gradient(circle,rgba(16,183,127,0.1) 0%,transparent 65%)",
        filter: "blur(36px)",
        transition: "background 0.5s",
        animation: "pulseGold 4s ease-in-out infinite",
      }} />

      {/* 3D tilt wrapper */}
      <div style={{ perspective: 900, perspectiveOrigin: "50% 50%",
        width: SIZE + 160, height: SIZE + 160,
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative",
      }}>
        <div style={{
          position: "relative", width: "100%", height: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: hovered
            ? "transform 0.08s linear"
            : "transform 0.6s cubic-bezier(0.16,1,0.3,1)",
          transformStyle: "preserve-3d",
        }}>
          {/* Orbital rings */}
          {ORBS.map((o, i) => (
            <div key={i} style={{
              position: "absolute",
              width: o.w, height: o.h,
              borderRadius: "50%",
              border: `${o.dashed ? "1px dashed" : "1.5px solid"} ${o.border}`,
              boxShadow: !o.dashed ? `0 0 14px ${o.border.replace("0.", "0.1")}` : "none",
              transform: o.tilt !== "none" ? o.tilt : undefined,
              animation: `spin ${o.spin} linear infinite${o.rev ? " reverse" : ""}`,
            }}>
              {o.dot && (
                <div style={{
                  position: "absolute", top: -o.dot.s / 2, left: "50%",
                  width: o.dot.s, height: o.dot.s, borderRadius: "50%",
                  background: `radial-gradient(circle,white,${o.dot.c})`,
                  boxShadow: `0 0 ${o.dot.s * 1.6}px ${o.dot.c}, 0 0 ${o.dot.s * 3}px ${o.dot.c}60`,
                  transform: "translateX(-50%)",
                }} />
              )}
            </div>
          ))}

          {/* Coin */}
          <div style={{
            position: "relative", zIndex: 4,
            animation: "coinFloat 5.5s ease-in-out infinite",
            filter: "drop-shadow(0 0 55px rgba(16,183,127,0.6)) drop-shadow(0 28px 70px rgba(0,0,0,0.85))",
            transform: `scale(${hovered ? 1.07 : 1})`,
            transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          }}>
            <div style={{ animation: "coin3DSpin 7s ease-in-out infinite" }}>
              <Coin3D size={SIZE} />
            </div>
            {hovered && (
              <div style={{
                position: "absolute", inset: -24,
                borderRadius: "50%",
                background: "radial-gradient(circle,rgba(16,183,127,0.18) 0%,transparent 70%)",
                animation: "pulseGold 1.2s ease-in-out infinite",
                pointerEvents: "none",
              }} />
            )}
          </div>

          {/* Floating particles along orbit paths */}
          {[
            { a: 0,   r: 175, s: 8,  c: "#10B77F", sp: 9  },
            { a: 72,  r: 158, s: 6,  c: "#60A5FA", sp: 13 },
            { a: 144, r: 180, s: 5,  c: "#A855F7", sp: 11 },
            { a: 216, r: 162, s: 7,  c: "#10B77F", sp: 8  },
            { a: 288, r: 172, s: 5,  c: "#FBBF24", sp: 15 },
          ].map(({ a, r, s, c, sp }, i) => {
            const rad = (a * Math.PI) / 180;
            const px  = Math.cos(rad) * r;
            const py  = Math.sin(rad) * r;
            return (
              <div key={i} style={{
                position: "absolute",
                left: "50%", top: "50%",
                marginLeft: px - s / 2,
                marginTop:  py - s / 2,
                width: s, height: s,
                borderRadius: "50%",
                background: c,
                boxShadow: `0 0 ${s * 2}px ${c},0 0 ${s * 4}px ${c}55`,
                animation: `particleOrbit ${sp}s linear infinite`,
                transformOrigin: `${-px + s / 2}px ${-py + s / 2}px`,
                opacity: 0.85,
              }} />
            );
          })}
        </div>
      </div>

      {/* Floating data cards */}
      <div style={{
        position: "absolute", top: "6%", right: "-2%",
        padding: "10px 16px", borderRadius: 14,
        background: "rgba(8,8,14,0.9)",
        border: "1px solid rgba(16,183,127,0.3)",
        backdropFilter: "blur(16px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6),0 0 20px rgba(16,183,127,0.1)",
        animation: "coinFloat 7s ease-in-out 0.5s infinite",
        zIndex: 8,
      }}>
        <p style={{ fontSize: "0.52rem", fontFamily: "Space Grotesk,sans-serif", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 4 }}>BTC Price</p>
        <p style={{ fontSize: "0.9rem",  fontFamily: "Space Grotesk,sans-serif", fontWeight: 800, color: "#fff",      letterSpacing: "-0.02em" }}>$84,291</p>
        <p style={{ fontSize: "0.6rem",  fontFamily: "Space Grotesk,sans-serif", color: "#10B77F", fontWeight: 700 }}>↑ +3.2% today</p>
      </div>

      <div style={{
        position: "absolute", bottom: "12%", left: "-4%",
        padding: "10px 16px", borderRadius: 14,
        background: "rgba(8,8,14,0.9)",
        border: "1px solid rgba(96,165,250,0.25)",
        backdropFilter: "blur(16px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6),0 0 20px rgba(96,165,250,0.1)",
        animation: "coinFloat 8s ease-in-out 2s infinite",
        zIndex: 8,
      }}>
        <p style={{ fontSize: "0.52rem", fontFamily: "Space Grotesk,sans-serif", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 4 }}>AI Signal</p>
        <p style={{ fontSize: "0.8rem",  fontFamily: "Space Grotesk,sans-serif", fontWeight: 800, color: "#60A5FA" }}>BUY ₿</p>
        <p style={{ fontSize: "0.58rem", fontFamily: "Space Grotesk,sans-serif", color: "rgba(255,255,255,0.4)" }}>94% confidence</p>
      </div>

      <div style={{
        position: "absolute", top: "44%", left: "-8%",
        padding: "8px 14px", borderRadius: 12,
        background: "rgba(8,8,14,0.9)",
        border: "1px solid rgba(168,85,247,0.25)",
        backdropFilter: "blur(16px)",
        animation: "coinFloat 6s ease-in-out 1.2s infinite",
        zIndex: 8,
      }}>
        <p style={{ fontSize: "0.52rem", fontFamily: "Space Grotesk,sans-serif", fontWeight: 700, letterSpacing: "0.1em", color: "rgba(255,255,255,0.35)", marginBottom: 3 }}>Portfolio</p>
        <p style={{ fontSize: "0.84rem", fontFamily: "Space Grotesk,sans-serif", fontWeight: 800, color: "#10B77F" }}>+18.4%</p>
      </div>
    </div>
  );
}

// ─── Stat Pill ────────────────────────────────────────────────────────────────
function StatPill({ icon, text, delay = 0 }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      className="anim-fade-in"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "8px 16px", borderRadius: 999,
        background: hov ? "rgba(16,183,127,0.08)" : "rgba(10,10,16,0.8)",
        border: `1px solid ${hov ? "rgba(16,183,127,0.35)" : "rgba(255,255,255,0.07)"}`,
        backdropFilter: "blur(10px)",
        fontSize: "0.78rem", color: hov ? "var(--text)" : "var(--text-dim)",
        fontWeight: 500, fontFamily: "Space Grotesk, sans-serif",
        animationDelay: `${delay}ms`, opacity: 0,
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        transition: "all 0.3s cubic-bezier(0.16,1,0.3,1)",
        cursor: "default",
      }}
    >
      <span>{icon}</span>
      <span>{text}</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LaunchBannerSection() {
  const [time, setTime] = useState(calcTimeLeft);
  const mouseRef        = useRef({ x: -9999, y: -9999 });
  const sectionRef      = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setTime(calcTimeLeft()), 1000);
    return () => clearInterval(t);
  }, []);

  // Track mouse over the whole section so canvas particles respond
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const onMove = (e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
    const onLeave = () => { mouseRef.current = { x: -9999, y: -9999 }; };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const launched   = !time.days && !time.hours && !time.minutes && !time.seconds;
  const totalSec   = (LAUNCH_DATE - new Date("2026-03-16T00:00:00")) / 1000;
  const leftSec    = Math.max(0, (LAUNCH_DATE - Date.now()) / 1000);
  const progressPct = Math.min(100, Math.max(0, ((totalSec - leftSec) / totalSec) * 100));

  return (
    <section
      ref={sectionRef}
      className="banner-section"
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "100px 24px",
        background: "transparent",
        overflow: "hidden",
      }}
    >
      {/* ── Particle network canvas (fills entire section) ── */}
      <ParticleCanvas mouseRef={mouseRef} />

      {/* Ambient radial glows */}
      <div style={{
        position: "absolute", top: "8%", left: "12%",
        width: 600, height: 600, borderRadius: "50%",
        background: "radial-gradient(circle,rgba(16,183,127,0.08) 0%,transparent 65%)",
        filter: "blur(80px)", pointerEvents: "none",
        animation: "auroraFloat1 16s ease-in-out infinite",
        zIndex: 2,
      }} />
      <div style={{
        position: "absolute", bottom: "10%", right: "10%",
        width: 500, height: 500, borderRadius: "50%",
        background: "radial-gradient(circle,rgba(96,165,250,0.07) 0%,transparent 65%)",
        filter: "blur(80px)", pointerEvents: "none",
        animation: "auroraFloat2 20s ease-in-out infinite",
        zIndex: 2,
      }} />
      <div style={{
        position: "absolute", top: "40%", right: "5%",
        width: 300, height: 300, borderRadius: "50%",
        background: "radial-gradient(circle,rgba(168,85,247,0.06) 0%,transparent 65%)",
        filter: "blur(60px)", pointerEvents: "none",
        zIndex: 2,
      }} />

      {/* Bottom fade */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: 200,
        background: "linear-gradient(to bottom,transparent,rgba(5,5,8,0.97))",
        pointerEvents: "none", zIndex: 5,
      }} />

      {/* ── Two-column grid ── */}
      <div
        className="banner-grid"
        style={{
          maxWidth: 1200, width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "clamp(32px, 5vw, 80px)",
          alignItems: "center",
          position: "relative", zIndex: 4,
        }}
      >
        {/* ── LEFT: text content ── */}
        <div>
          <div className="anim-fade-in d1 badge badge-gold"
            style={{ marginBottom: 28, display: "inline-flex", gap: 8 }}>
            <span style={{
              width: 7, height: 7, borderRadius: "50%",
              background: "var(--gold)", boxShadow: "0 0 10px var(--gold)",
              flexShrink: 0, animation: "pulseGold 2s ease infinite",
            }} />
            {launched ? "We've Launched!" : "Launching Soon"}
          </div>

          <h1 style={{
            fontFamily: "Space Grotesk, sans-serif",
            fontSize: "clamp(2.6rem, 5.5vw, 4.2rem)",
            fontWeight: 800, lineHeight: 1.04,
            letterSpacing: "-0.035em",
            marginBottom: 20, color: "var(--text)",
          }}>
            <span className="line-wrap">
              <span className="line-reveal" style={{ animationDelay: "0.05s" }}>Alpha Stack</span>
            </span>
            <span className="line-wrap">
              <span className="line-reveal" style={{ animationDelay: "0.18s" }}>
                {launched
                  ? <span className="gold-text shimmer-text">Is Live!</span>
                  : <span className="gold-text shimmer-text">Is Coming.</span>}
              </span>
            </span>
          </h1>

          <p className="anim-fade-up d3" style={{
            fontSize: "clamp(0.92rem, 1.6vw, 1.06rem)",
            color: "var(--text-dim)", lineHeight: 1.75,
            marginBottom: 40, maxWidth: 440,
          }}>
            The AI-powered investing platform that gives you institutional-grade
            market intelligence. Join early and get priority access on launch day.
          </p>

          {!launched ? (
            <>
              <p className="anim-fade-in d2" style={{
                fontFamily: "Space Grotesk, sans-serif",
                fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.24em",
                textTransform: "uppercase", color: "var(--gold)", marginBottom: 16,
              }}>
                ⏱ Launching in
              </p>

              <div className="anim-fade-up d3 banner-countdown" style={{
                display: "flex",
                gap: "clamp(8px, 1.8vw, 18px)",
                alignItems: "center",
                flexWrap: "wrap",
                marginBottom: 36,
              }}>
                <CountBlock value={time.days}    label="Days"  />
                <Colon />
                <CountBlock value={time.hours}   label="Hours" />
                <Colon />
                <CountBlock value={time.minutes} label="Mins"  />
                <Colon />
                <CountBlock value={time.seconds} label="Secs"  />
              </div>

              <div className="anim-fade-in d3" style={{ marginBottom: 36, maxWidth: 380 }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", marginBottom: 6,
                  fontFamily: "Space Grotesk, sans-serif",
                  fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.08em",
                  color: "var(--text-muted)", textTransform: "uppercase",
                }}>
                  <span>Launch Progress</span>
                  <span style={{ color: "var(--gold)" }}>{Math.round(progressPct)}%</span>
                </div>
                <div style={{
                  height: 5, borderRadius: 3,
                  background: "rgba(255,255,255,0.05)",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.04)",
                }}>
                  <div style={{
                    height: "100%", borderRadius: 3,
                    width: `${progressPct}%`,
                    background: "linear-gradient(90deg,var(--gold-dark),var(--gold),#6EE7B7)",
                    boxShadow: "0 0 14px rgba(16,183,127,0.55)",
                    transition: "width 1s ease",
                    position: "relative",
                  }}>
                    <div style={{
                      position: "absolute", top: 0, right: 0, bottom: 0, width: 40,
                      background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)",
                      animation: "shimmerSlide 2s ease-in-out infinite",
                    }} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="anim-fade-in" style={{
              padding: "18px 24px", borderRadius: 14, marginBottom: 36,
              background: "rgba(16,183,127,0.1)", border: "1px solid rgba(16,183,127,0.3)",
              color: "var(--gold)", fontFamily: "Space Grotesk, sans-serif",
              fontWeight: 700, fontSize: "1rem",
            }}>
              🚀 Alpha Stack is now live!
            </div>
          )}

          <div className="anim-fade-up d4 banner-cta" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 32 }}>
            <a href="#features" style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "13px 30px", borderRadius: 12,
              background: "linear-gradient(135deg,var(--gold-light),var(--gold),var(--gold-dark))",
              color: "#050508",
              fontFamily: "Space Grotesk, sans-serif",
              fontWeight: 700, fontSize: "0.92rem",
              textDecoration: "none",
              boxShadow: "0 0 32px rgba(16,183,127,0.35),0 4px 16px rgba(0,0,0,0.4)",
              transition: "transform 0.25s cubic-bezier(0.16,1,0.3,1),box-shadow 0.25s",
              position: "relative", overflow: "hidden",
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px) scale(1.02)"; e.currentTarget.style.boxShadow = "0 8px 48px rgba(16,183,127,0.55),0 4px 16px rgba(0,0,0,0.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 0 32px rgba(16,183,127,0.35),0 4px 16px rgba(0,0,0,0.4)"; }}
            >
              Secure Your Spot →
            </a>
            <a href="#why" style={{
              display: "inline-flex", alignItems: "center",
              padding: "13px 26px", borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.03)",
              color: "var(--text-dim)",
              fontFamily: "Space Grotesk, sans-serif",
              fontWeight: 600, fontSize: "0.92rem",
              textDecoration: "none",
              backdropFilter: "blur(10px)",
              transition: "all 0.25s cubic-bezier(0.16,1,0.3,1)",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(16,183,127,0.4)"; e.currentTarget.style.color = "var(--gold)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "var(--text-dim)"; e.currentTarget.style.transform = ""; }}
            >
              Learn More
            </a>
          </div>

          <div className="anim-fade-in d5 banner-pills" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <StatPill icon="👥" text="1,200+ on waitlist" delay={600} />
            <StatPill icon="🔒" text="No credit card"     delay={780} />
            <StatPill icon="⚡" text="Instant access"     delay={960} />
          </div>
        </div>

        {/* ── RIGHT: 3D Coin Scene ── */}
        <div
          className="anim-fade-in d2 banner-coin"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            opacity: 0,
          }}
        >
          <CoinScene3D mouseRef={mouseRef} />
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{
        position: "absolute", bottom: 28, left: "50%",
        transform: "translateX(-50%)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
        zIndex: 6, animation: "fadeIn 1s ease 1.8s both",
      }}>
        <span style={{
          fontSize: "0.55rem", letterSpacing: "0.2em", textTransform: "uppercase",
          color: "var(--text-muted)", fontFamily: "Space Grotesk, sans-serif",
        }}>
          Scroll to explore
        </span>
        <div style={{
          width: 20, height: 32, borderRadius: 10,
          border: "1px solid rgba(16,183,127,0.25)",
          display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "4px 0",
        }}>
          <div style={{
            width: 3, height: 7, borderRadius: 2,
            background: "var(--gold)",
            animation: "scrollBounce 1.8s ease-in-out infinite",
          }} />
        </div>
      </div>
    </section>
  );
}
